import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  getAdminLogs,
  getLogsByUser,
  getLogsByDateRange,
  getLogsByAction,
  getLogsByUserAndDateRange,
} from "../../api/parkpass/parkpassLogs";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const levelColors = {
  INFO: "blue",
  WARNING: "orange",
  ERROR: "red",
  DEBUG: "default",
  CRITICAL: "volcano",
};

const methodColors = {
  GET: "green",
  POST: "blue",
  PUT: "orange",
  PATCH: "cyan",
  DELETE: "red",
};

const CitizenLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    userId: "",
    action: undefined,
  });
  const [dateRange, setDateRange] = useState([]);

  const fetchLogs = useCallback(
    async (
      page = pagination.current,
      pageSize = pagination.pageSize,
      currentFilters = filters,
      dates = dateRange
    ) => {
      try {
        setLoading(true);
        const paginationParams = { page, pageSize };
        let response;

        const hasUser = currentFilters.userId && currentFilters.userId.trim();
        const hasDates = dates && dates.length === 2 && dates[0] && dates[1];
        const hasAction = currentFilters.action;
        const fromDate = hasDates ? dates[0].format("YYYY-MM-DD") : null;
        const toDate = hasDates ? dates[1].format("YYYY-MM-DD") : null;

        if (hasUser && hasDates) {
          response = await getLogsByUserAndDateRange(
            currentFilters.userId.trim(),
            fromDate,
            toDate,
            paginationParams
          );
        } else if (hasUser) {
          response = await getLogsByUser(
            currentFilters.userId.trim(),
            paginationParams
          );
        } else if (hasDates) {
          response = await getLogsByDateRange(fromDate, toDate, paginationParams);
        } else if (hasAction) {
          response = await getLogsByAction(hasAction, paginationParams);
        } else {
          response = await getAdminLogs(paginationParams);
        }

        setLogs(response.rows || []);
        setPagination({
          current: page,
          pageSize,
          total: response.totalRows || 0,
        });
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchLogs(1, 25, filters, dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchLogs(newPagination.current, newPagination.pageSize, filters, dateRange);
  };

  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize, filters, dateRange);
  };

  const handleReset = () => {
    const resetFilters = { userId: "", action: undefined };
    setFilters(resetFilters);
    setDateRange([]);
    fetchLogs(1, pagination.pageSize, resetFilters, []);
  };

  const columns = [
    {
      title: "Date/Heure",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (ts) => (ts ? new Date(ts).toLocaleString("fr-FR") : "N/A"),
    },
    {
      title: "Niveau",
      dataIndex: "level",
      key: "level",
      width: 90,
      render: (level) => (
        <Tag color={levelColors[level] || "default"}>{level}</Tag>
      ),
    },
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 90,
      render: (id) => id || "-",
    },
    {
      title: "IP",
      dataIndex: "ip",
      key: "ip",
      width: 130,
      render: (ip) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{ip || "-"}</span>
      ),
    },
    {
      title: "Requête",
      dataIndex: "request",
      key: "request",
      render: (req) => {
        if (!req) return "-";
        const parts = req.split(" ");
        const method = parts[0] || "";
        const path = parts.slice(1).join(" ");
        return (
          <Space>
            <Tag color={methodColors[method] || "default"}>{method}</Tag>
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>{path}</span>
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status_code",
      key: "status_code",
      width: 80,
      render: (code) => {
        if (!code) return "-";
        const color =
          code >= 500 ? "red" : code >= 400 ? "orange" : code >= 300 ? "blue" : "green";
        return <Tag color={color}>{code}</Tag>;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Logs Citizen Wallet
            </Title>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="User ID"
              value={filters.userId}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, userId: e.target.value }))
              }
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Action"
              style={{ width: "100%" }}
              value={filters.action}
              onChange={(v) => setFilters((prev) => ({ ...prev, action: v }))}
              allowClear
            >
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder={["Date début", "Date fin"]}
              value={dateRange.length ? dateRange : null}
              onChange={(dates) => setDateRange(dates || [])}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Rechercher
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey={(record, index) => `${record.timestamp}-${index}`}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} logs`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default CitizenLogsPage;
